import { Injectable } from '@angular/core';
import { AuthService } from 'app/core/services/auth.service';
import { BehaviorSubject, Observable, ReplaySubject, Subject, lastValueFrom, tap } from 'rxjs';
import {
  CreateStatementRequestParams,
  InternalService,
  ListStatements200Response,
  ListStatementsRequestParams,
  Statement,
} from '../../../../projects/tilled-api-client/src';
import { TilledAlert } from '../models/tilled-alert';
import { AlertService } from './alert.service';

@Injectable({
  providedIn: 'root',
})
export class StatementsAppService {
  errorMessage: string;
  constructor(
    private _internalService: InternalService,
    private _alertService: AlertService,
  ) {}

  public _statements$ = new Subject<ListStatements200Response>();
  private _statementsCount$ = new ReplaySubject<number>();
  private _statement$ = new Subject<Statement>();

  public statements$: Observable<ListStatements200Response> = this._statements$.asObservable();
  public statementsCount$: Observable<number> = this._statementsCount$.asObservable();
  public statement$: Observable<Statement> = this._statement$.asObservable();
  private accountHasStatements$ = new BehaviorSubject<boolean | null>(null);

  public listStatements(params: ListStatementsRequestParams): void {
    this._internalService
      .listStatements(params)
      .pipe(tap((result) => this._statementsCount$.next(result.total)))
      .subscribe({
        next: (statements) => {
          this._statements$.next(statements);
        },
        error: (err) => {
          // generic catch all for error responses
          const message: TilledAlert = {
            message: 'Could not load all customers',
            title: 'Server error',
            type: 'error',
          };
          this._alertService.showAlert(message);
        },
      });
  }

  public createStatement(params: CreateStatementRequestParams): void {
    this._internalService.createStatement(params).subscribe({
      next: (statement) => {
        const message: TilledAlert = {
          message: 'Commission statement was created successfully.',
          title: 'Commission statement created',
          type: 'success',
          timer: 8000,
        };
        this._alertService.showAlert(message);
        this._statement$.next(statement);
      },
      error: (err) => {
        // generic catch all for error responses
        const message: TilledAlert = {
          message: 'Could not create statement',
          title: 'Server error',
          type: 'error',
        };
        this._alertService.showAlert(message);
      },
    });
  }

  // Used to check if the `Commissions` navigation item should be shown or not. (No statements = no navigation item)
  public async doesAccountHaveStatements(): Promise<boolean> {
    // Return value if already set (to avoid multiple API calls)
    if (this.accountHasStatements$.getValue() !== null) {
      return this.accountHasStatements$.getValue();
    }
    const params: ListStatementsRequestParams = {
      tilledAccount: AuthService.getCurrentAccountId(),
      offset: 0,
      limit: 1,
      type: Statement.TypeEnum.COMMISSION,
    };
    const response = await lastValueFrom(this._internalService.listStatements(params));
    this.accountHasStatements$.next(response.total > 0);
    return this.accountHasStatements$.getValue();
  }
}
